import authService from './authService';

let BASE_URL = process.env.REACT_APP_API_URL;

async function loadConfig() {
  try {
    const response = await fetch("/config.json");
    const config = await response.json();
    BASE_URL = config.API_URL || BASE_URL;
  } catch (error) {
    console.error("Failed to load config.json. Using default .env API URL", error);
  }
}

loadConfig().then(() => {});

export async function fetchDataApi(url: string, options: RequestInit = {}): Promise<any> {
  try {
    const response = await authService.apiCall(`${BASE_URL}/${url}`, { ...options });
    const data = await response.json();
    if (!response.ok || !data.success) {
      if (response.status === 401 || response.status === 403 || data.message === "Forbidden access") {
        throw new Error("Forbidden: You do not have permission to access this resource.");
      }
      throw new Error("Failed to fetch");
    }
    return data;
  } catch (error: any) {
    throw new Error(error?.message || 'Failed to fetch data');
  }
}

export async function fetchAllDataApi(options: RequestInit = {}): Promise<any> {
  try {
    const response = await authService.apiCall(`${BASE_URL}/risks/getAll`, { ...options });
    const data = await response.json();
    if (!response.ok) {
      if (response.status === 401 || response.status === 403 || data.message === "Forbidden access") {
        throw new Error("Forbidden: You do not have permission to access this resource.");
      }
      throw new Error("Failed to fetch");
    }
    return data;
  } catch (error: any) {
    throw new Error(error?.message || 'Failed to fetch data');
  }
}

/** Risks for the likelihood × impact matrix (same auth rules as Risk Catalog). */
export async function fetchRisksMatrixList(): Promise<any[]> {
  try {
    const response = await authService.apiCall(`${BASE_URL}/risks/getAll`, { method: 'GET' });
    const data = await response.json();
    if (!response.ok || data.success === false) {
      if (response.status === 401 || response.status === 403 || data.message === 'Forbidden access') {
        throw new Error('Forbidden: You do not have permission to access this resource.');
      }
      throw new Error(data.message || 'Failed to load risks');
    }
    return Array.isArray(data.data) ? data.data : [];
  } catch (error: any) {
    throw new Error(error?.message || 'Failed to fetch risks');
  }
}

/** Full risk graph for matrix detail modal (controls, impacts, categories, …). */
export async function fetchRiskMatrixDetail(id: string): Promise<any> {
  try {
    const response = await authService.apiCall(`${BASE_URL}/risks/${encodeURIComponent(id)}`, { method: 'GET' });
    const data = await response.json();
    if (!response.ok || data.success === false) {
      if (response.status === 401 || response.status === 403) {
        throw new Error('Forbidden: You do not have permission to access this resource.');
      }
      throw new Error(data.message || 'Failed to load risk');
    }
    return data.data;
  } catch (error: any) {
    throw new Error(error?.message || 'Failed to fetch risk');
  }
}

// 2026-05-12: New canonical Risk CRUD. Points at the real /risks endpoint
// (the previous functions targeted dropped /cbe-risks endpoints; pages were
// also deleted). Use these for Risk module pages.

export interface RiskCorePayload {
  name: string;
  description?: string;
  sub_process?: string;
  inherent_value?: string;          // 'low' | 'medium' | 'high' | etc.
  inherent_frequency?: number;
  inherent_financial_value?: number;
  event?: string | null;            // EventType UUID
  categoryIds?: string[];           // attached via RiskCategory join
  functionIds?: string[];           // attached via RiskFunction join
  processIds?: string[];            // attached via RiskProcess join
}

export async function createRiskApi(body: RiskCorePayload): Promise<any> {
  const response = await authService.apiCall(`${BASE_URL}/risks`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.message || 'Failed to create risk');
  return data?.data ?? data;
}

export async function updateRiskApi(id: string, body: Partial<RiskCorePayload>): Promise<any> {
  const response = await authService.apiCall(`${BASE_URL}/risks/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.message || 'Failed to update risk');
  return data?.data ?? data;
}

/** Soft delete. Backend route is DELETE /risks/:id. */
export async function deleteRiskApi(id: string): Promise<void> {
  const response = await authService.apiCall(`${BASE_URL}/risks/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data?.message || 'Failed to delete risk');
  }
}

/** Returns the full nested risk (controls, KRIs, incidents, functions, processes, …). */
export async function getRiskApi(id: string): Promise<any> {
  return fetchRiskMatrixDetail(id);
}

export async function fetchOneApi(url: string, options: RequestInit = {}): Promise<any> {
  try {
    const response = await authService.apiCall(`${BASE_URL}/${url}`, { ...options });
    const data = await response.json();
    if (!response.ok || !data.success) {
      if (response.status === 401 || response.status === 403 || data.message === "Forbidden access") {
        throw new Error("Forbidden: You do not have permission to access this resource.");
      }
      throw new Error("Failed to fetch");
    }
    return data;
  } catch (error: any) {
    console.error("Error in fetchOneApi:", error);
    throw new Error(error?.message || 'Failed to fetch data');
  }
}

export async function fetchAllCategoryDataApi(): Promise<any> {
  try {
    const response = await authService.apiCall(`${BASE_URL}/categories/getAll`, { method: 'GET' });
    const data = await response.json();
    if (!response.ok || !data.success) {
      if (response.status === 401 || response.status === 403 || data.message === "Forbidden access") {
        throw new Error("Forbidden: You do not have permission to access this resource.");
      }
      throw new Error("Failed to fetch");
    }
    return data;
  } catch (error: any) {
    throw new Error(error?.message || 'Failed to fetch data');
  }
}

export async function fetchAllAreasDataApi(): Promise<any> {
  try {
    const response = await authService.apiCall(`${BASE_URL}/impacted-areas`, { method: 'GET' });
    const data = await response.json();
    if (!response.ok || !data.success) {
      if (response.status === 401 || response.status === 403 || data.message === "Forbidden access") {
        throw new Error("Forbidden: You do not have permission to access this resource.");
      }
      throw new Error("Failed to fetch");
    }
    return data;
  } catch (error: any) {
    throw new Error(error?.message || 'Failed to fetch data');
  }
}

